/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Configure sync alerts", description = "Set triggers that watch each sync for failures, rejected rows, and other problems, and route notifications to email, Slack, SMS, PagerDuty, or a webhook.", lastUpdatedDate = new Date(1789603200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Configure sync alerts",
        description: "Set triggers that watch each sync for failures, rejected rows, and other problems, and route notifications to email, Slack, SMS, PagerDuty, or a webhook.",
        lastUpdatedDate: "Thu Sep 17 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "How alerting works",
          "depth": 0,
          "id": "how-alerting-works"
        }, {
          "value": "When notifications are sent",
          "depth": 1,
          "id": "when-notifications-are-sent"
        }, {
          "value": "Configure alerts",
          "depth": 0,
          "id": "configure-alerts"
        }, {
          "value": "Trigger types",
          "depth": 0,
          "id": "trigger-types"
        }, {
          "value": "Unexpected sync volume",
          "depth": 0,
          "id": "unexpected-sync-volume"
        }, {
          "value": "Set up recipients and channels",
          "depth": 0,
          "id": "set-up-recipients-and-channels"
        }, {
          "value": "Test a recipient",
          "depth": 1,
          "id": "test-a-recipient"
        }, {
          "value": "Send alerts to a webhook",
          "depth": 1,
          "id": "send-alerts-to-a-webhook"
        }, {
          "value": "Mute a trigger",
          "depth": 0,
          "id": "mute-a-trigger"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      em: "em",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      h2: "h2",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      ol: "ol",
      img: "img",
      a: "a",
      blockquote: "blockquote",
      h4: "h4",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {FeatureFlag} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    return _jsxs(_Fragment, {
      children: [_jsx(FeatureFlag, {
        featureName: "Advanced alerting"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "A sync alert fires when a sync enters a warning or critical state, and sends a notification to the people or channels you choose."
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams and admins monitoring sync health"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: "At least one sync."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Configure alerts once per destination, override them on the syncs that need it, add the recipients who should hear about them, then let Hightouch notify you when a sync breaks or degrades."
      }), "\n", _jsx(_components.h2, {
        id: "how-alerting-works",
        children: "How alerting works"
      }), "\n", _jsxs(_components.p, {
        children: ["Each sync is evaluated against a set of ", _jsx(_components.strong, {
          children: "triggers"
        }), " that watch for problems like failed runs, rejected rows, slow performance, or unexpected data drops. Two terms do the work:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Alert"
          }), " — the signal that a sync has entered a ", _jsx(_components.code, {
            children: "WARNING"
          }), " or ", _jsx(_components.code, {
            children: "CRITICAL"
          }), " state, based on a trigger crossing its threshold."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Notification"
          }), " — the message a recipient receives. Alerts always show in the Hightouch app; a notification only goes out if a recipient is subscribed at that severity."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Instead of showing only the last run's result, each sync shows a ", _jsx(_components.strong, {
          children: "rollup"
        }), " of its current alerting state: ", _jsx(_components.code, {
          children: "No alerts"
        }), " when everything is healthy, or the number of active alerts when one or more triggers are in a ", _jsx(_components.code, {
          children: "WARNING"
        }), " or ", _jsx(_components.code, {
          children: "CRITICAL"
        }), " state. This reflects whether the sync is healthy, degraded, or needs attention, regardless of when it last ran."]
      }), "\n", _jsx(_components.h3, {
        id: "when-notifications-are-sent",
        children: "When notifications are sent"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch sends one notification per trigger ", _jsx(_components.strong, {
          children: "status change"
        }), ", not per run, so a persistent problem doesn't notify you on every run. For example:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["A sync rejects rows and crosses the warning threshold. You receive one ", _jsx(_components.code, {
            children: "WARNING"
          }), " notification."]
        }), "\n", _jsx(_components.li, {
          children: "It keeps rejecting rows on later runs. No new notification."
        }), "\n", _jsxs(_components.li, {
          children: ["Rejections climb past the critical threshold. You receive one ", _jsx(_components.code, {
            children: "CRITICAL"
          }), " notification."]
        }), "\n", _jsx(_components.li, {
          children: "The sync recovers. You receive one recovery notification."
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "configure-alerts",
        children: "Configure alerts"
      }), "\n", _jsx(_components.p, {
        children: "Set alert triggers at two levels. Destination-level settings apply to every sync using that destination; sync-level settings override them for a single sync."
      }), "\n", _jsxs(_components.p, {
        children: ["To set destination-wide defaults, go to ", _jsx(_components.strong, {
          children: "Integrations > Alerting"
        }), ", open the ", _jsx(_components.strong, {
          children: "Syncs"
        }), " tab, and select a destination. Set its triggers and recipients, and every sync using that destination inherits them."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/alerting/destination-alert-settings.png",
          alt: "Destination-level alert settings under Integrations > Alerting"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To override the defaults for one sync, open the sync and click the ", _jsx(_components.strong, {
          children: "Alerting"
        }), " tab. A sync inherits its destination's defaults; adjust its triggers or recipients here to override them."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/alerting/sync-alert-settings.png",
          alt: "Sync-level alert settings on the Alerting tab"
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Changes take effect the next time thresholds are evaluated: after the next\nsync run, or about every five minutes for time-based triggers."
        })
      }), "\n", _jsx(_components.h2, {
        id: "trigger-types",
        children: "Trigger types"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Fatal errors"
        }), ", ", _jsx(_components.strong, {
          children: "Rejected rows"
        }), ", and ", _jsx(_components.strong, {
          children: "Sync throughput"
        }), " come preconfigured with defaults. ", _jsx(_components.strong, {
          children: "Model or audience size"
        }), " and ", _jsx(_components.strong, {
          children: "Sync duration"
        }), " start off until you configure them."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/alerting/alerting-triggers.png",
          alt: "The trigger list showing default alert conditions"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Trigger"
            }), _jsx(_components.th, {
              children: "What it watches"
            }), _jsx(_components.th, {
              children: "Default"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Fatal errors"
              })
            }), _jsx(_components.td, {
              children: "Runs that can't complete because of an infrastructure or configuration problem, such as unreachable credentials or invalid SQL."
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "CRITICAL"
              }), " if any run has a fatal error."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Rejected rows"
              })
            }), _jsx(_components.td, {
              children: "Rows the destination rejected because of schema mismatches, validation failures, or destination constraints."
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "WARNING"
              }), " if any rows are rejected during a run."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Sync throughput"
              })
            }), _jsx(_components.td, {
              children: "Successful operations over a time window, to catch stalled or idle syncs."
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "WARNING"
              }), " if no successful operations occur in seven days."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Model or audience size"
              })
            }), _jsx(_components.td, {
              children: "The number of rows the model returns at run time, which can flag broken upstream filters or joins."
            }), _jsx(_components.td, {
              children: "Off until you set a minimum row count."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Sync duration"
              })
            }), _jsx(_components.td, {
              children: "Wall-clock runtime, from when a run is queued to when it finishes."
            }), _jsx(_components.td, {
              children: "Off until you set a duration threshold."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Unexpected sync volume"
              })
            }), _jsx(_components.td, {
              children: "Journey trigger syncs that are about to sync far more members than entered the journey node since the last run."
            }), _jsx(_components.td, {
              children: "Fires when a run would sync more than twice the members who entered the node, and more than 1,000 extra rows."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Unexpected sync volume"
        }), " applies only to journey sync tiles in ", _jsx(_components.a, {
          href: "/customer-studio/journeys/tiles#trigger-mode",
          children: "Trigger mode"
        }), ", and is evaluated after a run plans but before it sends anything, rather than after the run ends, so its ", _jsx(_components.code, {
          children: "STOP"
        }), " severity stops the run instead of reporting on one that already happened. See ", _jsx(_components.a, {
          href: "#unexpected-sync-volume",
          children: "Unexpected sync volume"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Fatal-error and rejected-row triggers are evaluated right after each run ends. Time-based triggers — throughput and duration — are evaluated about every five minutes, so they can fire before a long-running run finishes even if it's working normally. To adjust a trigger, click the pencil icon on it."
      }), "\n", _jsx(_components.h2, {
        id: "unexpected-sync-volume",
        children: "Unexpected sync volume"
      }), "\n", _jsxs(_components.p, {
        children: ["Applies to journey sync tiles in ", _jsx(_components.a, {
          href: "/customer-studio/journeys/tiles#trigger-mode",
          children: "Trigger mode"
        }), ". It compares the members a run is about to sync against the members who entered the tile since the last run, and acts before any rows reach the destination: at ", _jsx(_components.code, {
          children: "STOP"
        }), " it prevents the run from sending, at ", _jsx(_components.code, {
          children: "WARNING"
        }), " it notifies you and lets the run continue."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/alerting/journey-sync-volume-trigger.png",
          alt: "The Unexpected sync volume trigger on a journey trigger sync"
        })
      }), "\n", _jsx(_components.p, {
        children: "A stopped run reports what it saw, for example:"
      }), "\n", _jsxs(_components.blockquote, {
        children: ["\n", _jsx(_components.p, {
          children: "Stopped before syncing: About to sync 50000 members but only 1200 entered this node since the last run (41.7x). Planned adds 50000, retried 0, unaccounted 48800."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "A gap that size usually means the run is about to sync members who have already been through the node."
      }), "\n", _jsxs(_components.p, {
        children: ["A stopped run doesn't resume. To allow the next run to proceed, mute the trigger on the sync's ", _jsx(_components.a, {
          href: "/syncs/alerting#configure-alerts",
          children: "Alerting"
        }), " tab, which lets subsequent runs complete and silences the notification."]
      }), "\n", _jsxs(_components.p, {
        children: ["If the alert fires on runs that are normal for this journey, raise either threshold, or set the trigger to ", _jsx(_components.code, {
          children: "WARNING"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "set-up-recipients-and-channels",
        children: "Set up recipients and channels"
      }), "\n", _jsxs(_components.p, {
        children: ["Recipients are the people and channels that receive notifications. Manage them on the ", _jsx(_components.strong, {
          children: "Recipients"
        }), " tab of ", _jsx(_components.strong, {
          children: "Integrations > Alerting"
        }), ", where each channel type is set up once:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Email"
          }), " — add an address directly."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Slack"
          }), " — authorize the Slack connection, then choose a channel."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "PagerDuty"
          }), " — complete the PagerDuty setup, then route alerts to a service."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "SMS"
          }), " — add a phone number. Hightouch covers the cost of SMS alerts."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Webhook"
          }), " — send a JSON payload to a URL you specify. See ", _jsx(_components.a, {
            href: "#send-alerts-to-a-webhook",
            children: "Send alerts to a webhook"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/alerting/recipient-channels.png",
          alt: "The Recipients tab showing channel types and a recipient's settings"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For each recipient, choose how much to send under ", _jsx(_components.strong, {
          children: "Alert severity"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Send all alerts"
          }), " — notify on both ", _jsx(_components.code, {
            children: "WARNING"
          }), " and ", _jsx(_components.code, {
            children: "CRITICAL"
          }), " status changes."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Send critical alerts only"
          }), " — notify on ", _jsx(_components.code, {
            children: "CRITICAL"
          }), " status changes only."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can also scope a recipient to specific destinations, and still override that routing per sync from the sync's ", _jsx(_components.strong, {
          children: "Alerting"
        }), " tab."]
      }), "\n", _jsx(_components.h3, {
        id: "test-a-recipient",
        children: "Test a recipient"
      }), "\n", _jsxs(_components.p, {
        children: ["Before you rely on a recipient, confirm it works. On the recipient's settings, click ", _jsx(_components.strong, {
          children: "Send a test alert now"
        }), " to send a test notification through that channel."]
      }), "\n", _jsx(_components.h3, {
        id: "send-alerts-to-a-webhook",
        children: "Send alerts to a webhook"
      }), "\n", _jsxs(_components.p, {
        children: ["A webhook channel delivers a JSON payload to a URL you specify whenever an alert fires or recovers. Webhook channels support custom HTTP headers, sent with every request, for authentication tokens or routing keys your endpoint requires — for example ", _jsx(_components.code, {
          children: "Authorization: Bearer <token>"
        }), " or ", _jsx(_components.code, {
          children: "X-Routing-Key: ops-team"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/alerting/webhook-recipient-config.png",
          alt: "Webhook recipient configuration showing custom headers"
        })
      }), "\n", _jsx(_components.h4, {
        id: "webhook-payload",
        children: "Webhook payload"
      }), "\n", _jsxs(_components.p, {
        children: ["When a sync alert fires or recovers, Hightouch sends an HTTP ", _jsx(_components.code, {
          children: "POST"
        }), " request with a JSON body to your webhook URL. The request carries ", _jsx(_components.code, {
          children: "Content-Type: application/json"
        }), ", along with any custom headers you configured. The body identifies the sync, the status change, and the trigger that caused it:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-json",
          children: [_jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resourceId\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resourceType\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Sync\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resourceName\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"[Orders → Salesforce]\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resourceUrl\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"https://app.hightouch.com/example-workspace/syncs/123\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resourceDescription\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Syncs orders to Salesforce.\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"resourceLabels\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"team\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"data-platform\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"destinationLabels\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"team\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"growth\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"newStatus\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"critical\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"oldStatus\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"healthy\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"trigger\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "{"
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"displayName\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"Fatal errors\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"status\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"critical\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ","
          }), "\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "\"displayMessage\""
          }), _jsx(_components.span, {
            className: "hljs-punctuation",
            children: ":"
          }), " ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"3 or more consecutive sync runs encountered fatal errors.\""
          }), "\n  ", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n", _jsx(_components.span, {
            className: "hljs-punctuation",
            children: "}"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Field"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "resourceId"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The ID of the sync that generated the alert."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "resourceType"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The monitored resource type. Sync alerts send ", _jsx(_components.code, {
                children: "Sync"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "resourceName"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The sync name, formatted as ", _jsx(_components.code, {
                children: "[model name → destination name]"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "resourceUrl"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "A link to the sync in the Hightouch app."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "resourceDescription"
              })
            }), _jsx(_components.td, {
              children: "string | null"
            }), _jsx(_components.td, {
              children: "The sync's description, when it has one, or null if the description is not set."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "resourceLabels"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "object"
              })
            }), _jsxs(_components.td, {
              children: ["The sync's labels as key-value string pairs. Empty (", _jsx(_components.code, {
                children: "{}"
              }), ") when the sync has no labels."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "destinationLabels"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "object"
              })
            }), _jsxs(_components.td, {
              children: ["The labels on the sync's destination, as key-value string pairs. Empty (", _jsx(_components.code, {
                children: "{}"
              }), ") when the destination has no labels."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "newStatus"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The sync's new status: ", _jsx(_components.code, {
                children: "warning"
              }), " or ", _jsx(_components.code, {
                children: "critical"
              }), " when an alert fires, or ", _jsx(_components.code, {
                children: "healthy"
              }), " when it recovers."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "oldStatus"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The sync's previous status, or ", _jsx(_components.code, {
                children: "disabled"
              }), " if the sync was disabled before the latest status. Omitted if the sync has never changed status."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "trigger"
              })
            }), _jsx(_components.td, {
              children: "object"
            }), _jsx(_components.td, {
              children: "The trigger whose status change generated the alert."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "trigger.displayName"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The trigger's name, such as ", _jsx(_components.code, {
                children: "Fatal errors"
              }), ", ", _jsx(_components.code, {
                children: "Rejected rows"
              }), ", or ", _jsx(_components.code, {
                children: "Sync throughput"
              }), ". Matches the ", _jsx(_components.a, {
                href: "#trigger-types",
                children: "trigger types"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "trigger.status"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The trigger's current status: ", _jsx(_components.code, {
                children: "warning"
              }), ", ", _jsx(_components.code, {
                children: "critical"
              }), ", or ", _jsx(_components.code, {
                children: "healthy"
              }), " when it recovers."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "trigger.displayMessage"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "A human-readable description of the alert or recovery."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "trigger.error"
              })
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "Additional error detail, included only when the alert has one."
            })]
          })]
        })]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "The payload body is fixed. You can configure the webhook URL and custom\nheaders, but not the fields Hightouch sends or their shape."
        })
      }), "\n", _jsx(_components.h2, {
        id: "mute-a-trigger",
        children: "Mute a trigger"
      }), "\n", _jsx(_components.p, {
        children: "Mute a trigger to stop its notifications during planned maintenance, a known issue, or quiet hours. A muted trigger still updates the sync's alert state and appears in rollups; it just doesn't notify anyone."
      }), "\n", _jsxs(_components.p, {
        children: ["Muting ", _jsx(_components.strong, {
          children: "Unexpected sync volume"
        }), " also lets the next run proceed. This trigger can stop a run, so silencing it and allowing it are the same action."]
      }), "\n", _jsxs(_components.p, {
        children: ["From the sync's ", _jsx(_components.strong, {
          children: "Alerting"
        }), " tab, click the volume icon on the trigger and select a mute duration — a fixed window such as one hour or one day, or indefinitely. You can also mute from the ", _jsx(_components.strong, {
          children: "Mute..."
        }), " link at the top of an active alert on the sync page."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
